import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Button,
  Stack,
  Snackbar,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";
import validator from "validator";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import { EIdType } from "../../enums/EIdType";
import { IBranch } from "../../interfaces/IBranch";
import { IService } from "../../interfaces/IService";
import { IServiceGroup } from "../../interfaces/IServiceGroup";
import { IBranchGroup } from "../../interfaces/IBranchGroup";
import { CApiRestful } from "../../api/CApiRestful";
import ImgBooked from "../../assets/booked.png";
import ModPhoneInput from "../mods/ModPhoneInput";
import { CDataUtil } from "../../utils/CDataUtil";
import ModApptDetail from "../mods/ModApptDetail";
import enTranslation from "../../i18n/en/translation.json";
import msTranslation from "../../i18n/ms/translation.json";

const TAG = "TabMakeAppointment";

interface IProps {
  selectedLanguage: string;
  mobileNumber: string;
  jsonWsGetServiceListResp: any;
  jsonWsGetBranchListResp: any;
  onAppointmentMade?: () => void;
  signature?: string;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const useStyles = makeStyles({
  root: {},
  consent: {
    whiteSpace: "pre-line",
  },
});

const TabMakeAppointment = (props: IProps) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedIdType, setSelectedIdType] = useState<EIdType>(EIdType.NONE);
  const [isIdTypeEnabled, setIsIdTypeEnabled] = useState(false);
  const [filteredBranchList, setFilteredBranchList] = useState<IBranch[]>([]);
  const [selectedApptDate, setSelectedApptDate] = useState<Date | null>(null);
  const [apptTimeList, setApptTimeList] = useState<string[]>([]);
  const [apptTimeError, setApptTimeError] = useState("---");
  const [selectedApptTime, setSelectedApptTime] = useState<string | undefined>(
    undefined
  );
  const [serviceGroupList, setServiceGroupList] = useState<IServiceGroup[]>([]);
  const [selectedServiceGroupId, setSelectedServiceGroupId] = useState<
    number | undefined
  >(undefined);
  const [serviceList, setServiceList] = useState<IService[]>([]);
  const [filteredServiceList, setFilteredServiceList] = useState<IService[]>(
    []
  );
  const [selectedServiceNo, setSelectedServiceNo] = useState<
    number | undefined
  >(undefined);
  const [branchList, setBranchList] = useState<IBranch[]>([]);
  const [selectedBranchCode, setSelectedBranchCode] = useState<string>("0");
  const [phoneNumber, setPhoneNumber] = useState(
    props.mobileNumber.length > 0 && props.mobileNumber.substring(0, 1) !== "0"
      ? props.mobileNumber
      : ""
  );
  const [isShowBookingDlg, setIsShowBookingDlg] = useState(false);
  const [bookedApptCode, setBookedApptCode] = useState("");
  const [bookedApptDate, setBookedApptDate] = useState("");
  const [bookedApptTime, setBookedApptTime] = useState("");
  const [firstName, setFirstName] = useState("");
  const [firstNameHelperText, setFirstNameHelperText] = useState("");
  const [lastName, setLastName] = useState("");
  const [lastNameHelperText, setLastNameHelperText] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [emailAddrHelperText, setEmailAddrHelperText] = useState("");
  const [icOrPassportNumber, setIcOrPassportNumber] = useState("");
  const [policyOrCertNumber, setPolicyOrCertNumber] = useState("");
  const [policyOrCertNumberHelperText, setPolicyOrCertNumberHelperText] =
    useState("");
  const [remarks, setRemarks] = useState("");
  const [remarksHelperText, setRemarksHelperText] = useState("");
  const [selectedAppointmentType, setSelectedAppointmentType] = useState("---");

  const resetForm = () => {
    try {
      setFirstName("");
      setLastName("");
      setEmailAddr("");
      if (
        props.mobileNumber.length > 0 &&
        props.mobileNumber.substring(0, 1) !== "0"
      ) {
        setPhoneNumber(props.mobileNumber);
      } else {
        setPhoneNumber("");
      }

      setSelectedIdType(EIdType.NONE);
      setIcOrPassportNumber("");
      setPolicyOrCertNumber("");
      setRemarks("");
      setSelectedApptDate(null);
      setApptTimeList([]);
      setApptTimeError("---");
      setSelectedApptTime(undefined);
      setSelectedServiceNo(undefined);
      setSelectedBranchCode("0");
      setBookedApptCode("");
      setBookedApptDate("");
      setBookedApptTime("");
      setIsShowBookingDlg(false);
      setSelectedAppointmentType("---");
      setSelectedServiceGroupId(undefined);
      setFilteredServiceList([]);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (props.jsonWsGetServiceListResp) {
      refreshServiceGroupList();
    }
  }, [props.jsonWsGetServiceListResp]);

  useEffect(() => {
    if (props.jsonWsGetServiceListResp) {
      refreshServiceGroupList();
    }
    if (props.jsonWsGetBranchListResp) {
      refreshBranchList();
    }
  }, [props.selectedLanguage]);

  useEffect(() => {
    if (props.jsonWsGetServiceListResp) {
      refreshServiceList();
    }
  }, [selectedServiceGroupId]);

  useEffect(() => {
    if (props.jsonWsGetBranchListResp) {
      refreshBranchList();
    }
  }, [selectedServiceNo]);

  const refreshServiceList = () => {
    try {
      if (!selectedServiceGroupId) {
        return;
      }

      const _filteredServiceList: IService[] = [];
      for (let _svc of serviceList) {
        if (_svc.serviceGroupIdList.includes(selectedServiceGroupId)) {
          _filteredServiceList.push(_svc);
        }
      }
      setFilteredServiceList(_filteredServiceList);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const autoDetectServiceGrpNameLanguage = (serviceGrpName: string) => {
    var finalServiceGrpName = serviceGrpName;

    try {
      if (serviceGrpName == enTranslation.makeAppt.serviceGrpGeneral) {
        // General
        finalServiceGrpName = t("makeAppt.serviceGrpGeneral");
      } else if (serviceGrpName == enTranslation.makeAppt.serviceGrpLifeFamily) {
        // Life & Family
        finalServiceGrpName = t("makeAppt.serviceGrpLifeFamily");
      } else if (serviceGrpName == enTranslation.makeAppt.serviceGrpGroupPolicy) {
        // Group Policy
        finalServiceGrpName = t("makeAppt.serviceGrpGroupPolicy");
      } 
    } catch (err: any) {
      setError(err.message);
    }

    return finalServiceGrpName;
  };

  const refreshServiceGroupList = () => {
    try {
      const _serviceGroupList: IServiceGroup[] = [];
      const _serviceList: IService[] = [];
      for (let service of props.jsonWsGetServiceListResp.GetServiceListResp
        .ServiceList.Service) {
        var serviceName = service.IndService.ServiceName;
        const serviceNo = service.IndService.ServiceNo;
        const languageStringList = service.LanguageStringList?.LanguageString;

        const displayStringValue =
          CDataUtil.extractDisplayStringValueByLanguageId(
            languageStringList,
            props.selectedLanguage
          );
        if (displayStringValue.length > 0) {
          serviceName = displayStringValue;
        }

        var finalBranchGroupIdList: number[] = [];
        var finalServiceGroupIdList: number[] = [];
        const serviceGrpList = service.ServiceGrpList.ServiceGrp;
        if (serviceGrpList.length > 0) {
          for (let serviceGrp of serviceGrpList) {
            const indServiceGrp = serviceGrp.IndServiceGrp;

            finalServiceGroupIdList = finalServiceGroupIdList.concat(
              parseInt(indServiceGrp.ServiceGrpId)
            );

            const _filteredServiceGroupList = _serviceGroupList.filter(
              (_serviceGroup) =>
                _serviceGroup.serviceGroupId == indServiceGrp.ServiceGrpId
            );
            if (_filteredServiceGroupList.length === 0) {
              _serviceGroupList.push({
                serviceGroupId: indServiceGrp.ServiceGrpId,
                serviceGroupName: autoDetectServiceGrpNameLanguage(indServiceGrp.ServiceGrpName),
              });
            }

            const _branchGroupIdList =
              CDataUtil.extractBranchGrpList(serviceGrp);

            finalBranchGroupIdList =
              finalBranchGroupIdList.concat(_branchGroupIdList);
          }
        } else {
          const indServiceGrp = serviceGrpList.IndServiceGrp;

          finalServiceGroupIdList = finalServiceGroupIdList.concat(
            parseInt(indServiceGrp.ServiceGrpId)
          );

          const _filteredServiceGroupList = _serviceGroupList.filter(
            (_serviceGroup) =>
              _serviceGroup.serviceGroupId == indServiceGrp.ServiceGrpId
          );
          if (_filteredServiceGroupList.length === 0) {
            _serviceGroupList.push({
              serviceGroupId: indServiceGrp.ServiceGrpId,
              serviceGroupName: autoDetectServiceGrpNameLanguage(indServiceGrp.ServiceGrpName),
            });
          }

          const _branchGroupIdList =
            CDataUtil.extractBranchGrpList(serviceGrpList);

          finalBranchGroupIdList =
            finalBranchGroupIdList.concat(_branchGroupIdList);
        }

        _serviceList.push({
          serviceName: serviceName,
          serviceNo: serviceNo,
          branchGroupIdList: finalBranchGroupIdList,
          serviceGroupIdList: finalServiceGroupIdList,
        });
      }
      setServiceGroupList(_serviceGroupList);
      setServiceList(_serviceList);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const refreshBranchList = () => {
    try {
      const _branchGroupList: IBranchGroup[] = [];
      const _branchList: IBranch[] = [];
      if (
        props.jsonWsGetBranchListResp.GetBranchListResp.BranchList.Branch
          .length > 0
      ) {
        for (let branch of props.jsonWsGetBranchListResp.GetBranchListResp
          .BranchList.Branch) {
          var branchName = branch.IndBranch.BranchName;
          const branchCode = branch.IndBranch.BranchCode;
          const languageStringList = branch.LanguageStringList?.LanguageString;

          const displayStringValue =
            CDataUtil.extractDisplayStringValueByLanguageId(
              languageStringList,
              props.selectedLanguage
            );
          if (displayStringValue.length > 0) {
            branchName = displayStringValue;
          }

          const indBranchGrp = branch.BranchGrpList.BranchGrp.IndBranchGrp;
          if (
            !_branchGroupList.find(
              (o) => o.branchGroupId === indBranchGrp.BranchGrpId
            )
          ) {
            _branchGroupList.push({
              branchGroupId: indBranchGrp.BranchGrpId,
            });
          }

          _branchList.push({
            branchCode: branchCode,
            branchName: branchName,
            branchGroupId: indBranchGrp.BranchGrpId,
            branchGroupName: indBranchGrp.BranchGrpName,
          });
        }
      } else {
        const singleBranch =
          props.jsonWsGetBranchListResp.GetBranchListResp.BranchList.Branch;
        var branchName = singleBranch.IndBranch.BranchName;
        const branchCode = singleBranch.IndBranch.BranchCode;
        const languageStringList =
          singleBranch.LanguageStringList?.LanguageString;

        const displayStringValue =
          CDataUtil.extractDisplayStringValueByLanguageId(
            languageStringList,
            props.selectedLanguage
          );
        if (displayStringValue.length > 0) {
          branchName = displayStringValue;
        }

        const indBranchGrp = singleBranch.BranchGrpList.BranchGrp.IndBranchGrp;
        if (
          !_branchGroupList.find(
            (o) => o.branchGroupId === indBranchGrp.BranchGrpId
          )
        ) {
          _branchGroupList.push({
            branchGroupId: indBranchGrp.BranchGrpId,
          });
        }

        _branchList.push({
          branchCode: branchCode,
          branchName: branchName,
          branchGroupId: indBranchGrp.BranchGrpId,
          branchGroupName: indBranchGrp.BranchGrpName,
        });
      }

      _branchList.sort((a, b) => {
        return a.branchName.localeCompare(b.branchName);
      });

      setBranchList(_branchList);

      if (selectedServiceNo) {
        const selectedService = filteredServiceList.filter(
          (service) => service.serviceNo == selectedServiceNo
        );

        const _filteredBranchList = _branchList.filter((branch) =>
          selectedService[0].branchGroupIdList.includes(branch.branchGroupId)
        );

        // Extra filtering which is hardcoded for virtual and branch services
        const virtualOnlyService = (window as any).app.config
          .VirtualOnlyService;
        const branchOnlyService = (window as any).app.config.BranchOnlyService;
        const virtualOrBranchService = (window as any).app.config
          .VirtualOrBranchService;
        const virtualBranch = (window as any).app.config.VirtualBranch;

        var _finalFilteredBranchList: IBranch[] = [];
        if (virtualOnlyService.includes(selectedServiceNo)) {
          // Show virtual branch only

          _finalFilteredBranchList = _filteredBranchList.filter((branch) =>
            virtualBranch.includes(branch.branchCode)
          );
        } else if (branchOnlyService.includes(selectedServiceNo)) {
          // Show physical branch only

          _finalFilteredBranchList = _filteredBranchList.filter(
            (branch) => !virtualBranch.includes(branch.branchCode)
          );
        } else if (virtualOrBranchService.includes(selectedServiceNo)) {
          // Show both virtual and physical branch
          _finalFilteredBranchList = _filteredBranchList;
        }

        setFilteredBranchList(_finalFilteredBranchList);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const isAllowSubmit = () => {
    var isAllow = false;

    try {
      if (
        selectedServiceNo &&
        selectedBranchCode !== "0" &&
        selectedApptDate != null &&
        selectedApptTime &&
        phoneNumber.length > 2 &&
        firstName.length > 0 &&
        lastName.length > 0 &&
        emailAddr.length > 0
      ) {
        isAllow = true;
      }
    } catch (err: any) {
      setError(err.message);
    }

    return isAllow;
  };

  const fetchMakeAppt = async () => {
    try {
      if (
        !selectedServiceNo ||
        !selectedApptTime ||
        phoneNumber.length === 0 ||
        selectedBranchCode === "0" ||
        firstName.length === 0 ||
        lastName.length === 0 ||
        emailAddr.length === 0
      ) {
        return;
      }

      if (emailAddrHelperText.length !== 0) {
        setError(t("makeAppt.invalidEmail"));
        return;
      }

      if (phoneNumber.length < 10 || phoneNumber.length > 14) {
        setError(t("makeAppt.invalidPhoneNumber"));
        return;
      }

      if (firstNameHelperText.length !== 0) {
        setError(t("makeAppt.invalidFirstName"));
        return;
      }

      if (lastNameHelperText.length !== 0) {
        setError(t("makeAppt.invalidLastName"));
        return;
      }

      if (policyOrCertNumberHelperText.length !== 0) {
        setError(t("makeAppt.invalidPolicyOrCertNumber"));
        return;
      }

      if (remarksHelperText.length !== 0) {
        setError(t("makeAppt.invalidRemark"));
        return;
      }

      var icNumber;
      var passportNumber;
      switch (selectedIdType) {
        case EIdType.NRIC:
          icNumber = icOrPassportNumber;
          break;
        case EIdType.PASSPORT_ARMY_POLICE_ID:
          passportNumber = icOrPassportNumber;
          break;
        default:
          break;
      }

      setIsLoading(true);
      const jsonMakeApptResp = await CApiRestful.makeAppt(
        selectedBranchCode,
        selectedServiceNo,
        selectedApptTime,
        phoneNumber,
        phoneNumber,
        firstName,
        lastName,
        emailAddr,
        selectedAppointmentType,
        icNumber,
        passportNumber,
        policyOrCertNumber,
        remarks,
        props.signature
      );

      if (jsonMakeApptResp.root) {
        if (jsonMakeApptResp.root.ErrorList) {
          if (jsonMakeApptResp.root.ErrorList.Error) {
            setError(jsonMakeApptResp.root.ErrorList.Error.Reason);
            return;
          } else {
            setError(t("makeAppt.errorMakeAppt"));
            return;
          }
        } else {
          setError(t("makeAppt.errorMakeAppt"));
          return;
        }
      } else if (jsonMakeApptResp.UpdateApptListResp) {
        if (jsonMakeApptResp.UpdateApptListResp.ErrorList) {
          if (jsonMakeApptResp.UpdateApptListResp.ErrorList.Error) {
            setError(
              jsonMakeApptResp.UpdateApptListResp.ErrorList.Error.Reason
            );
            return;
          } else {
            setError(t("makeAppt.errorMakeAppt"));
            return;
          }
        } else if (jsonMakeApptResp.UpdateApptListResp.Status != 0) {
          setError(t("makeAppt.errorMakeAppt"));
          return;
        }
      }

      const appt = jsonMakeApptResp.UpdateApptListResp.ApptList.Appt;
      const _apptDate: string = appt.ApptDate;
      const _apptTime: string = appt.ApptTime;
      const _apptCode: string = appt.IndAppt.ApptCode;

      setBookedApptCode(_apptCode);
      setBookedApptDate(_apptDate);
      setBookedApptTime(_apptTime);

      setIsShowBookingDlg(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box>
        <Typography fontWeight={"bold"} fontSize={"32px"}>
          {t("makeAppt.makeAppointmentTitle")}
        </Typography>
      </Box>
      <Box>
        <Typography fontSize={"16px"}>
          {t("makeAppt.makeAppointmentSubtitle")}
        </Typography>
      </Box>

      <Box pt={4}>
        <Box
          border={1}
          borderRadius={2}
          p={2}
          borderColor="grey.400"
          bgcolor="grey.200"
        >
          <Box sx={{ wordWrap: "break-word" }}>
            <Linkify>{t("makeAppt.ads1")}</Linkify>
          </Box>
          <Box sx={{ wordWrap: "break-word" }} pt={2}>
            <Linkify>{t("makeAppt.ads2")}</Linkify>
          </Box>
        </Box>
      </Box>

      <Box pt={5}>
        <Typography fontSize={"20px"} fontWeight={"bold"}>
          {t("makeAppt.contactDetails")}
        </Typography>
      </Box>

      <Box color={"#ff0000"} textAlign="right">
        {"* " + t("makeAppt.compulsory")}
      </Box>

      <Box pt={2}>
        <TextField
          required
          label={t("makeAppt.firstName")}
          variant="outlined"
          size="small"
          autoFocus={true}
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            maxLength: 150,
          }}
          value={firstName}
          onChange={(e) => {
            const _firstName = e.target.value;
            if (
              !validator.isAlphanumeric(_firstName, undefined, {
                ignore: " ",
              })
            ) {
              setFirstNameHelperText(t("makeAppt.invalidFirstName"));
            } else {
              setFirstNameHelperText("");
            }

            if (_firstName.length === 0) {
              setFirstNameHelperText("");
            }
            setFirstName(_firstName);
          }}
          helperText={firstNameHelperText}
          error={firstNameHelperText.length > 0}
          fullWidth
        />
      </Box>
      <Box pt={2}>
        <TextField
          required
          label={t("makeAppt.lastName")}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            maxLength: 150,
          }}
          size="small"
          value={lastName}
          onChange={(e) => {
            const _lastName = e.target.value;
            if (
              !validator.isAlphanumeric(_lastName, undefined, {
                ignore: " ",
              })
            ) {
              setLastNameHelperText(t("makeAppt.invalidLastName"));
            } else {
              setLastNameHelperText("");
            }

            if (_lastName.length === 0) {
              setLastNameHelperText("");
            }
            setLastName(_lastName);
          }}
          helperText={lastNameHelperText}
          error={lastNameHelperText.length > 0}
          fullWidth
        />
      </Box>

      <Box pt={2}>
        <TextField
          required
          label={t("makeAppt.email")}
          variant="outlined"
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            maxLength: 50,
          }}
          value={emailAddr}
          onChange={(e) => {
            const _emailAddr = e.target.value;
            if (!validator.isEmail(_emailAddr)) {
              setEmailAddrHelperText(t("makeAppt.invalidEmail"));
            } else {
              setEmailAddrHelperText("");
            }

            if (_emailAddr.length === 0) {
              setEmailAddrHelperText("");
            }

            setEmailAddr(_emailAddr);
          }}
          helperText={emailAddrHelperText}
          error={emailAddrHelperText.length > 0}
          fullWidth
        />
      </Box>

      <Box pt={2}>
        <ModPhoneInput
          value={phoneNumber}
          onChange={(_phoneNumber: string) => {
            setPhoneNumber(_phoneNumber);
          }}
          disabled={
            props.mobileNumber.length > 0 &&
            props.mobileNumber.substring(0, 1) !== "0"
              ? true
              : false
          }
        />
      </Box>

      <Box pt={3}>
        <Typography fontSize={"20px"} fontWeight={"bold"}>
          {t("makeAppt.policyDetails")}
        </Typography>
      </Box>
      <Box color={"#ff0000"} textAlign="right">
        {"* " + t("makeAppt.compulsory")}
      </Box>

      <Box pt={2}>
        <TextField
          placeholder={t("makeAppt.policyNum")}
          variant="outlined"
          size="small"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            maxLength: 50,
          }}
          value={policyOrCertNumber}
          onChange={(e) => {
            const _policyOrCertNumber = e.target.value;
            if (
              !validator.isAlphanumeric(_policyOrCertNumber, undefined, {
                ignore: " ",
              })
            ) {
              setPolicyOrCertNumberHelperText(
                t("makeAppt.invalidPolicyOrCertNumber")
              );
            } else {
              setPolicyOrCertNumberHelperText("");
            }

            if (_policyOrCertNumber.length === 0) {
              setPolicyOrCertNumberHelperText("");
            }
            setPolicyOrCertNumber(_policyOrCertNumber);
          }}
          helperText={policyOrCertNumberHelperText}
          error={policyOrCertNumberHelperText.length > 0}
          fullWidth
        />
      </Box>

      <Box pt={2}>
        <Box p={1} border={1} borderColor={"#BBBBBB"} borderRadius={1}>
          <Box pl={1} pr={1}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">
                <Box display="flex">
                  <Box>{t("makeAppt.typeOfInsurance")}</Box>
                  <Box color="red" pl={1}>
                    {"*"}
                  </Box>
                </Box>
              </FormLabel>
              {serviceGroupList.length > 0 ? (
                <RadioGroup
                  name="radio-buttons-group"
                  value={selectedServiceGroupId ?? 0}
                  onChange={(e) => {
                    setSelectedServiceGroupId(parseInt(e.target.value));
                    setSelectedServiceNo(undefined);
                    setSelectedBranchCode("0");
                    setSelectedApptDate(null);
                    setSelectedApptTime(undefined);
                    setSelectedAppointmentType("---");
                  }}
                >
                  {serviceGroupList.map((serviceGroup) => (
                    <FormControlLabel
                      key={serviceGroup.serviceGroupId}
                      value={serviceGroup.serviceGroupId}
                      control={<Radio size="small" />}
                      label={serviceGroup.serviceGroupName}
                    />
                  ))}
                </RadioGroup>
              ) : (
                <Box textAlign="center">
                  {"-- " + t("makeAppt.none") + " --"}
                </Box>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box pt={2}>
        <Box p={1} border={1} borderColor={"#BBBBBB"} borderRadius={1}>
          <Box pl={1} pr={1}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">
                <Box display="flex">
                  <Box>{t("makeAppt.visitPurpose")}</Box>
                  <Box color="red" pl={1}>
                    {"*"}
                  </Box>
                </Box>
              </FormLabel>
              {filteredServiceList.length > 0 ? (
                <RadioGroup
                  name="radio-buttons-group"
                  value={selectedServiceNo ?? 0}
                  onChange={(e) => {
                    setSelectedServiceNo(parseInt(e.target.value));
                    setSelectedBranchCode("0");
                    setSelectedApptDate(null);
                    setSelectedApptTime(undefined);
                    setSelectedAppointmentType("---");
                  }}
                >
                  {filteredServiceList.map((service) => (
                    <FormControlLabel
                      key={service.serviceNo}
                      value={service.serviceNo}
                      control={<Radio size="small" />}
                      label={service.serviceName}
                    />
                  ))}
                </RadioGroup>
              ) : (
                <Box textAlign="center">
                  {"-- " + t("makeAppt.none") + " --"}
                </Box>
              )}
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box pt={2}>
        <TextField
          placeholder={t("makeAppt.remarks")}
          variant="outlined"
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            maxLength: 255,
          }}
          size="small"
          value={remarks}
          onChange={(e) => {
            const _remarks = e.target.value;
            if (!validator.isAscii(_remarks)) {
              setRemarksHelperText(t("makeAppt.invalidRemark"));
            } else {
              setRemarksHelperText("");
            }

            if (_remarks.length === 0) {
              setRemarksHelperText("");
            }
            setRemarks(_remarks);
          }}
          multiline={true}
          minRows={2}
          helperText={remarksHelperText}
          error={remarksHelperText.length > 0}
          fullWidth
        />
      </Box>

      {selectedServiceNo && (
        <ModApptDetail
          jsonWsGetServiceListResp={props.jsonWsGetServiceListResp}
          jsonWsGetBranchListResp={props.jsonWsGetBranchListResp}
          selectedLanguage={props.selectedLanguage}
          selectedServiceNo={selectedServiceNo}
          selectedBranchCode={selectedBranchCode}
          onError={(err: any) => {
            setError(err.message);
          }}
          onChange={(
            branchCode?: string,
            apptDate?: Date,
            apptTime?: string,
            appointmentType?: string
          ) => {
            if (branchCode && branchCode.length > 0) {
              setSelectedBranchCode(branchCode);
            }

            if (apptDate) {
              setSelectedApptDate(apptDate);
              setSelectedApptTime(undefined);
            }

            if (apptTime && apptTime.length > 0) {
              setSelectedApptTime(apptTime);
            }

            if (appointmentType && appointmentType.length > 0) {
              setSelectedAppointmentType(appointmentType);
            }
          }}
        />
      )}

      <Box pt={4} pb={8}>
        <Button
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            fontSize: "16px",
            height: "56px",
            borderRadius: "5px",
          }}
          disabled={!isAllowSubmit()}
          fullWidth
          variant="contained"
          onClick={() => {
            fetchMakeAppt();
          }}
        >
          {t("makeAppt.submit")}
        </Button>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Stack spacing={2} sx={{ width: "100%" }}>
        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          open={error.length > 0}
          onClose={() => setError("")}
        >
          <Alert
            onClose={() => setError("")}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Stack>

      <Dialog open={isShowBookingDlg} keepMounted>
        <DialogTitle></DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box textAlign="center">
              <img src={ImgBooked} height={131} />
            </Box>
            <Box
              fontSize={"20px"}
              fontWeight={"bold"}
              color={"#000000"}
              pt={2}
              textAlign="center"
            >
              {t("makeAppt.bookingTitle")}
            </Box>
            <Box fontSize={"16px"} color={"#000000"} textAlign="center">
              <Box>{t("makeAppt.bookingSuccessDesc1")}</Box>
              <Box fontWeight={"bold"}>{"<" + emailAddr + ">"}</Box>
              <Box>{t("makeAppt.bookingSuccessDesc2")}</Box>
            </Box>
            <Box pt={5}>
              {props.mobileNumber.length > 0 && (
                <Button
                  disableElevation
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "16px",
                    height: "56px",
                    borderRadius: "5px",
                    lineHeight: "18px",
                  }}
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    resetForm();
                    if (props.onAppointmentMade) {
                      props.onAppointmentMade();
                    }
                  }}
                >
                  {t("root.viewAppointment")}
                </Button>
              )}
              <Button
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "16px",
                  height: "56px",
                  backgroundColor: "transparent",
                  marginTop: "16px",
                  color: "#04AAFF",
                }}
                fullWidth
                variant="contained"
                onClick={() => {
                  resetForm();
                }}
              >
                {t("makeAppt.back")}
              </Button>
            </Box>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TabMakeAppointment;
